// Leveling — seniority-tilt shared constants + helpers.
//
// The Leveling intake tab exposes a single Junior <-> Executive slider per
// (discipline, channel). Its value is a `shift` in [-1, +1] (0 = model
// default). The engine reshapes the base `levelWeights` row by a smooth,
// bounded multiplier keyed to how senior each level is, then renormalizes —
// so the discipline/channel total FTE is preserved, the shape is kept, and
// levels with 0 base weight stay 0 (leaders keep starting at L4+).
//
// Buckets exist purely for UI labels + the resulting-mix readout.

import { LEVEL_IDS } from '../types.js';
import type { LevelId } from '../types.js';

export type LevelBucketId = 'junior' | 'management' | 'executive';

export interface LevelBucketDef {
  id: LevelBucketId;
  label: string;
  levels: LevelId[];
}

/** Junior = L1-L3, Management = L4-L6, Executive = L7-L8. */
export const LEVEL_BUCKETS: readonly LevelBucketDef[] = [
  { id: 'junior', label: 'Junior', levels: ['L1', 'L2', 'L3'] },
  { id: 'management', label: 'Management', levels: ['L4', 'L5', 'L6'] },
  { id: 'executive', label: 'Executive', levels: ['L7', 'L8'] },
] as const;

/**
 * Strength of the seniority tilt at the slider extremes. Calibrated for a
 * *gentle* shift: at full Executive (`shift = 1`) the executive bucket share
 * grows only modestly (roughly a third above its base share on a typical
 * pyramid), not a collapse to one bucket. Bump this up for a more aggressive
 * feel, down for subtler.
 */
export const SENIORITY_TILT_STRENGTH = 0.07;

/** Midpoint rank of L1..L8. Cancels under normalization; kept only so the
 *  exponent stays small + symmetric. */
const RANK_PIVOT = (1 + LEVEL_IDS.length) / 2;

/** 1-indexed rank of a level (L1 = 1 .. L8 = 8). Unknown levels rank 0. */
export function levelRank(levelId: string): number {
  const idx = LEVEL_IDS.indexOf(levelId as LevelId);
  return idx < 0 ? 0 : idx + 1;
}

/** Clamp a raw slider value to the [-1, +1] shift range. */
export function clampShift(shift: number | undefined): number {
  const s = Number(shift) || 0;
  return Math.max(-1, Math.min(1, s));
}

/** Sanitize a configurable tilt strength: fall back to the default when
 *  missing/invalid, clamp to a sane non-negative range. */
export function resolveTiltStrength(strength: number | undefined): number {
  if (strength === undefined || strength === null || !Number.isFinite(Number(strength))) {
    return SENIORITY_TILT_STRENGTH;
  }
  return Math.max(0, Math.min(1, Number(strength)));
}

/**
 * Reweight a base `weights` map by the bounded exponential seniority tilt.
 * Result is NOT normalized (callers normalize before use / display). Levels
 * with 0 base weight stay 0. `strength` overrides the default tilt
 * aggressiveness (e.g. from `Model.seniorityTiltStrength`).
 */
export function tiltWeights(
  weights: Partial<Record<LevelId, number>>,
  shift: number,
  strength?: number,
): Partial<Record<LevelId, number>> {
  const s = clampShift(shift);
  const k = resolveTiltStrength(strength);
  const out: Partial<Record<LevelId, number>> = {};
  for (const [lvl, w] of Object.entries(weights) as [LevelId, number][]) {
    const base = Number(w) || 0;
    if (base <= 0) {
      out[lvl] = 0;
      continue;
    }
    if (s === 0 || k === 0) {
      out[lvl] = base;
      continue;
    }
    const mult = Math.exp(k * s * (levelRank(lvl) - RANK_PIVOT));
    out[lvl] = base * mult;
  }
  return out;
}
